import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Divider,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  createFilterOptions,
  Grid
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import Loader from './Loader';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';

const filter = createFilterOptions();

function ProductEditModal({ open, onClose, product, onSave, isSaving, setToast }) {
  const [editedProduct, setEditedProduct] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [attributeName, setAttributeName] = useState(null); // Oggetto per Autocomplete
  const [attributeValue, setAttributeValue] = useState(null); // Oggetto per Autocomplete
  const [isMandatory, setIsMandatory] = useState(false);

  const isNewProduct = !product?.id;

  const attributes = useLiveQuery(
    () => product?.id ? db.productAttributes.where('prodotto_id').equals(product.id).toArray() : [],
    [product?.id]
  );

  const existingAttributes = useLiveQuery(() => db.attributi.toArray());
  const [existingValues, setExistingValues] = useState([]);

  useEffect(() => {
    const fetchValues = async () => {
      if (attributeName && attributeName.id) {
        const values = await db.attributiValori.where('attributo_id').equals(attributeName.id).toArray();
        setExistingValues(values);
        // Se l'attributo esiste già, imposta lo stato obbligatorio dal DB (se presente)
        setIsMandatory(attributeName.obbligatorio === 1 || attributeName.obbligatorio === true);
      } else {
        setExistingValues([]);
      }
    };
    fetchValues();
  }, [attributeName]);

  useEffect(() => {
    if (product) {
      // Mapping fallbacks per supportare vecchi campi
      setEditedProduct({
        ...product,
        nome: product.nome || product.name || '',
        descrizione_breve: product.descrizione_breve || product.description || '',
        descrizione_lunga: product.descrizione_lunga || '',
        prezzo: product.prezzo || product.price || '',
        imageUrl: product.imageUrl || '',
        sku: product.sku || '',
        slug: product.slug || '',
        visibile: product.visibile === 1 || product.visibile === true,
        in_evidenza: product.in_evidenza === 1 || product.in_evidenza === true,
      });
    } else {
      setEditedProduct(null);
    }
    // Resetta sempre i campi degli attributi all'apertura/chiusura o cambio prodotto
    setAttributeName(null);
    setAttributeValue(null);
    setIsMandatory(false);
    setIsDirty(false);
  }, [product]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    setEditedProduct(prev => ({ ...prev, [name]: finalValue }));
    setIsDirty(true);
  };

  const handleSave = () => {
    onSave(editedProduct);
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  const handleAddAttribute = async () => {
    if (!attributeName || !attributeValue || !product?.id) return;

    let finalAttrId = attributeName.id;
    let finalValId = attributeValue.id;

    try {
      // 1. Se l'attributo è nuovo, crealo
      if (!finalAttrId) {
        const name = attributeName.nome || attributeName.inputValue || attributeName;
        const response = await fetch('https://deadapi.techysumo.it/techyshop/addAttribute', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify({ 
            nome: name,
            obbligatorio: isMandatory ? 1 : 0 
          }),
        });
        const result = await response.json();
        if (result.result) {
          finalAttrId = result.details.id || result.details;
          await db.attributi.add({ 
            id: finalAttrId, 
            nome: name,
            obbligatorio: isMandatory ? 1 : 0
          });
        } else {
          throw new Error("Errore durante la creazione dell'attributo");
        }
      }

      // 2. Se il valore è nuovo, crealo
      if (!finalValId) {
        const value = attributeValue.valore || attributeValue.inputValue || attributeValue;
        const response = await fetch('https://deadapi.techysumo.it/techyshop/addAttributoValore', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify({ attributo_id: finalAttrId, valore: value }),
        });
        const result = await response.json();
        if (result.result) {
          finalValId = result.details.id || result.details;
          await db.attributiValori.add({ id: finalValId, attributo_id: finalAttrId, valore: value });
        } else {
          throw new Error("Errore durante la creazione del valore");
        }
      }

      // 3. Collega l'attributo e il valore al prodotto
      const linkResponse = await fetch('https://deadapi.techysumo.it/techyshop/addAttributoProdotto', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({
          prodotto_id: product.id,
          attributo_id: finalAttrId,
          valore_id: finalValId
        })
      });

      const linkResult = await linkResponse.json();
      if (linkResult.result) {
        await db.productAttributes.add({
          prodotto_id: product.id,
          name: attributeName.nome || attributeName.inputValue || attributeName,
          value: attributeValue.valore || attributeValue.inputValue || attributeValue
        });
        
        setAttributeName(null);
        setAttributeValue(null);
        setIsMandatory(false);
        setToast({ open: true, message: 'Attributo aggiunto con successo!', severity: 'success' });
      } else {
        throw new Error("Errore durante il collegamento dell'attributo al prodotto");
      }

    } catch (error) {
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    }
  };

  const handleDeleteAttribute = async (attrId) => {
    try {
      // In una versione reale, dovresti chiamare anche l'API per rimuovere il collegamento
      await db.productAttributes.delete(attrId);
      setToast({ open: true, message: 'Attributo rimosso.', severity: 'info' });
    } catch (error) {
      setToast({ open: true, message: 'Errore durante la rimozione.', severity: 'error' });
    }
  };

  if (!editedProduct) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isNewProduct ? 'Crea Nuovo Prodotto' : `Modifica Prodotto: ${editedProduct.nome}`}
      </DialogTitle>
      <DialogContent dividers>
        <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome Prodotto"
                name="nome"
                value={editedProduct.nome}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Prezzo"
                name="prezzo"
                value={editedProduct.prezzo}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="SKU"
                name="sku"
                value={editedProduct.sku}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Slug"
                name="slug"
                value={editedProduct.slug}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="URL Immagine"
                name="imageUrl"
                value={editedProduct.imageUrl}
                onChange={handleChange}
                margin="normal"
              />
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <FormControlLabel
                  control={<Checkbox name="visibile" checked={!!editedProduct.visibile} onChange={handleChange} color="primary" />}
                  label="Visibile"
                />
                <FormControlLabel
                  control={<Checkbox name="in_evidenza" checked={!!editedProduct.in_evidenza} onChange={handleChange} color="primary" />}
                  label="In Evidenza"
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrizione Breve"
                name="descrizione_breve"
                value={editedProduct.descrizione_breve}
                onChange={handleChange}
                margin="normal"
                multiline
                rows={2}
              />
              <TextField
                fullWidth
                label="Descrizione Lunga"
                name="descrizione_lunga"
                value={editedProduct.descrizione_lunga}
                onChange={handleChange}
                margin="normal"
                multiline
                rows={4}
              />
            </Grid>
          </Grid>

          {!isNewProduct && (
            <>
              <Divider sx={{ my: 4 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Attributi Prodotto</Typography>
              
              {!existingAttributes || !attributes ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                  <Loader />
                  <Typography sx={{ mt: 2 }}>Caricamento attributi...</Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 3, mt: 2 }}>
                    <Autocomplete
                      value={attributeName}
                      onChange={(event, newValue) => {
                        if (typeof newValue === 'string') {
                          setAttributeName({ nome: newValue });
                        } else if (newValue && newValue.inputValue) {
                          setAttributeName({ nome: newValue.inputValue });
                        } else {
                          setAttributeName(newValue);
                        }
                      }}
                      filterOptions={(options, params) => {
                        const filtered = filter(options, params);
                        const { inputValue } = params;
                        const isExisting = options.some((option) => inputValue === option.nome);
                        if (inputValue !== '' && !isExisting) {
                          filtered.push({
                            inputValue,
                            nome: `Aggiungi "${inputValue}"`,
                          });
                        }
                        return filtered;
                      }}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      options={existingAttributes || []}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        if (option.inputValue) return option.inputValue;
                        return option.nome || '';
                      }}
                      renderOption={(props, option) => <li {...props}>{option.nome}</li>}
                      sx={{ flex: 1 }}
                      freeSolo
                      renderInput={(params) => <TextField {...params} label="Nome Attributo" size="small" />}
                    />

                    <Autocomplete
                      value={attributeValue}
                      onChange={(event, newValue) => {
                        if (typeof newValue === 'string') {
                          setAttributeValue({ valore: newValue });
                        } else if (newValue && newValue.inputValue) {
                          setAttributeValue({ valore: newValue.inputValue });
                        } else {
                          setAttributeValue(newValue);
                        }
                      }}
                      filterOptions={(options, params) => {
                        const filtered = filter(options, params);
                        const { inputValue } = params;
                        const isExisting = options.some((option) => inputValue === option.valore);
                        if (inputValue !== '' && !isExisting) {
                          filtered.push({
                            inputValue,
                            valore: `Aggiungi "${inputValue}"`,
                          });
                        }
                        return filtered;
                      }}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      options={existingValues || []}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        if (option.inputValue) return option.inputValue;
                        return option.valore || '';
                      }}
                      renderOption={(props, option) => <li {...props}>{option.valore}</li>}
                      sx={{ flex: 1 }}
                      freeSolo
                      disabled={!attributeName}
                      renderInput={(params) => <TextField {...params} label="Valore" size="small" />}
                    />

                    <FormControlLabel
                      control={<Checkbox checked={isMandatory} onChange={(e) => setIsMandatory(e.target.checked)} color="primary" />}
                      label="Obblig."
                      sx={{ mt: -0.5 }}
                    />

                    <Button variant="contained" onClick={handleAddAttribute} disabled={!attributeName || !attributeValue}>
                      Aggiungi
                    </Button>
                  </Box>

                  <List dense sx={{ backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 1 }}>
                    {attributes.map((attr) => (
                      <ListItem
                        key={attr.id}
                        divider
                        secondaryAction={
                          <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteAttribute(attr.id)}>
                            <Delete />
                          </IconButton>
                        }
                      >
                        <ListItemText 
                          primary={attr.name} 
                          secondary={attr.value} 
                          primaryTypographyProps={{ sx: { fontWeight: 600, color: 'primary.main' } }}
                        />
                      </ListItem>
                    ))}
                    {attributes.length === 0 && (
                      <ListItem>
                        <ListItemText primary="Nessun attributo associato a questo prodotto." sx={{ opacity: 0.5, textAlign: 'center', py: 2 }} />
                      </ListItem>
                    )}
                  </List>
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={isSaving}>Annulla</Button>
        <Button onClick={handleSave} variant="contained" disabled={!isDirty || isSaving}>
          {isSaving ? 'Salvataggio...' : 'Salva Modifiche'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ProductEditModal;
